<template>
    <form
        v-if="paymentMethods && paymentMethods.cartoesdecredito"
        id="credit-card-form"
        class="pt-3"
    >
        <div id="card-brands" class="mb">
            <span
                v-if="$scope.ACCEPTED_PAYMENT_METHODS.indexOf('mastercard') !== -1"
                class="card-flag mastercard"
            ></span>
            <span
                v-if="$scope.ACCEPTED_PAYMENT_METHODS.indexOf('visa') !== -1"
                class="card-flag visa"
            ></span>
            <span
                v-if="$scope.ACCEPTED_PAYMENT_METHODS.indexOf('diners') !== -1"
                class="card-flag diners"
            ></span>
            <span
                v-if="$scope.ACCEPTED_PAYMENT_METHODS.indexOf('elo') !== -1"
                class="card-flag elo"
            ></span>
            <span
                v-if="$scope.ACCEPTED_PAYMENT_METHODS.indexOf('amex') !== -1"
                class="card-flag amex"
            ></span>
            <span
                v-if="$scope.ACCEPTED_PAYMENT_METHODS.indexOf('hipercard') !== -1"
                class="card-flag hipercard"
            ></span>
            <span
                v-if="$scope.ACCEPTED_PAYMENT_METHODS.indexOf('discover') !== -1"
                class="card-flag discover"
            ></span>
            <span
                v-if="$scope.ACCEPTED_PAYMENT_METHODS.indexOf('aura') !== -1"
                class="card-flag aura"
            ></span>
            <span
                v-if="$scope.ACCEPTED_PAYMENT_METHODS.indexOf('jcb') !== -1"
                class="card-flag jcb"
            ></span>
        </div>

        <div class="form-group">
            <label for="card-holder">{{ $t('_checkout._payment._card.name') }}</label>
            <input
                id="card-holder"
                v-model.trim="$v.creditCard.name.$model"
                :class="{ danger: $v.creditCard.name.$error }"
                autocomplete="cc-name"
                required
                type="text"
            />
        </div>
        <div id="card-number-wrapper" class="form-group">
            <label for="card-number">{{ $t('_checkout._payment._card.number') }}</label>
            <cleave
                id="card-number"
                v-model.trim="$v.creditCard.number.$model"
                :class="{ danger: $v.creditCard.number.$error }"
                :options="creditOptions"
                autocomplete="cc-number"
                inputmode="number"
                @change.native="checkPaymentIssuerMP"
                @keyup.native="checkPaymentIssuerMP"
            ></cleave>
            <span v-if="!creditCard.issuer" class="card-icon generic"></span>
            <span
                v-else-if="creditCard.issuer === 'mastercard' || creditCard.issuer === 'master'"
                class="card-icon mastercard"
            ></span>
            <span v-else-if="creditCard.issuer === 'visa'" class="card-icon visa"></span>
            <span v-else-if="creditCard.issuer === 'diners'" class="card-icon dinersclub"></span>
        </div>
        <div class="d-flex">
            <div class="flex-item-grow">
                <label class="d-block mb-2">{{ $t('_checkout._payment._card.expiration') }}:</label>
                <div class="d-flex">
                    <SelectField
                        id="card-expire"
                        v-model="creditCard.expiryMonth"
                        :class="{
                            'input-danger': $v.creditCard.expiryMonth.$error,
                        }"
                        class="flex-item-grow mr"
                    >
                        <option v-for="month in months" :value="month">
                            {{ month }}
                        </option>
                    </SelectField>
                    <SelectField
                        v-model="creditCard.expiryYear"
                        :class="{
                            'input-danger': $v.creditCard.expiryYear.$error,
                        }"
                        class="flex-item-grow mt-auto mr"
                    >
                        <option v-for="year in years" :value="year">
                            {{ year }}
                        </option>
                    </SelectField>
                </div>
            </div>
            <div class="form-group pos-relative" style="flex: 0 0 100px">
                <label for="card-cvv">CVV:</label>
                <cleave
                    id="card-cvv"
                    v-model.trim="$v.creditCard.cvv.$model"
                    :class="{ danger: $v.creditCard.cvv.$error }"
                    :options="cvvOptions"
                    autocomplete="cc-csc"
                    inputmode="number"
                ></cleave>
                <span id="card-cvv-icon"></span>
            </div>
        </div>

        <div class="form-group">
            <label for="card-installments" style="display: block">
                <span>{{ $t('_checkout._payment._card.selectInstallments') }}:</span>
                <span class="pull-right" v-if="minimumInstallmentTooltip">
                    <i
                        v-tooltip:left="minimumInstallmentTooltip"
                        class="icon-cr icon-question-sign grey"
                    ></i>
                </span>
            </label>
            <div v-if="paymentMethods" class="cr-select" data-preselect-term="">
                <select
                    id="card-installments"
                    v-model="creditCard.installments"
                    :class="{ danger: $v.creditCard.installments.$error }"
                >
                    <option
                        v-for="option in paymentMethods.cartoesdecredito"
                        :value="option.total_installments"
                    >
                        {{ option.total_installments }}x {{ $t('of') }}
                        {{ $f.formatMoney(option.installments[0]) }}
                        &nbsp;(Total
                        {{ $f.formatMoney(option.total) }}
                        <template v-if="option.interests > 0">
                            ,
                            {{
                                $t('_checkout._payment._card.interestsMonth', [
                                    formatNumber(option.interests, 2),
                                ])
                            }})
                        </template>
                        <template v-else>, {{ $t('_shop.interestFree') }})</template>
                    </option>
                </select>
            </div>
        </div>

        <div v-if="enableMercadopago" class="text-right mt">
            <img
                :src="`${$scope.CDN_IMAGES}/images/site/2018/checkout/mercado-pago-checkout.png`"
                alt="Processado por Mercado Pago"
                class="flex-child-fixed"
            />
        </div>
    </form>
</template>

<script>
    import { required, minValue, maxLength, minLength } from 'vuelidate/lib/validators';
    import CheckoutPaymentMethod from './CheckoutPaymentMethod';

    import { errorMessage } from './mercado-pago/helpers';
    import accounting from '../../../../helpers/accounting';
    import SelectField from '../../../../components/input/select-field/SelectField.vue';

    export default {
        components: { SelectField },

        extends: CheckoutPaymentMethod,

        name: 'CheckoutPaymentCredit',

        props: ['cart', 'customer', 'paymentMethods'],

        data() {
            const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
            const years = [];
            let year = new Date().getFullYear();
            let currentYear = year;
            while (currentYear - year <= 20) {
                years.push(currentYear++);
            }

            return {
                enableMercadopago: false,
                verifyingCard: false,
                creditOptions: {
                    creditCard: true,
                    onCreditCardTypeChanged: (type) => {
                        this.updatePaymentIssuer(type);
                    },
                },
                cvvOptions: {
                    numericOnly: true,
                    blocks: [4],
                },
                creditCard: {
                    name: undefined,
                    number: undefined,
                    issuer: undefined,
                    cvv: undefined,
                    expiryMonth: undefined,
                    expiryYear: undefined,
                    installments: 1,
                },
                months,
                years,
            };
        },

        computed: {
            description() {
                const installments =
                    (this.creditCard.installments || 1) === 1
                        ? this.$t('_shop.inCash')
                        : this.$tc('_shop.installmentsX', this.creditCard.installments || 1);
                return `${this.$t('_shop.creditCard')} - ${installments}`;
            },

            minimumInstallmentTooltip() {
                if (
                    this.$scope &&
                    this.$scope.CONFIG &&
                    this.$scope.CONFIG.CREDIT_CARD_MINIMUN_INSTALLMENT_VALUE > 0
                ) {
                    return (
                        'Valor mínimo de ' +
                        this.$f.formatMoney(
                            this.$scope.CONFIG.CREDIT_CARD_MINIMUN_INSTALLMENT_VALUE
                        ) +
                        ' em cada parcela'
                    );
                }

                return null;
            },
        },

        watch: {
            paymentMethods(to) {
                if (to) {
                    this.resetMethod();
                }
            },
        },

        methods: {
            resetMethod() {
                this.creditCard.installments = 1;
                this.verifyingCard = false;
            },

            formatNumber(value, precision) {
                return accounting.formatNumber(value, precision);
            },

            checkPaymentIssuerMP() {
                if (!this.enableMercadopago) return;

                if (!this.creditCard.number || this.creditCard.number.length < 6) {
                    this.creditCard.issuer = null;
                    return;
                }

                const bin = this.creditCard.number.replace(/[ .-]/g, '').slice(0, 6);

                const errorMsg =
                    'Erro identificar tipo de cartão. Por favor, verifique se as informações estão corretas e tente novamente';
                if (bin.length >= 6 && typeof window.Mercadopago !== 'undefined') {
                    window.Mercadopago.setPublishableKey(
                        this.$scope.checkoutPreferences.mercado_pago_key
                    );
                    window.Mercadopago.getPaymentMethod({ bin }, (status, response) => {
                        if (status === 200) {
                            this.creditCard.issuer = response[0].id;
                        } else {
                            this.$emit('error', errorMsg);
                        }
                    });
                }
            },

            updatePaymentIssuer(type) {
                if (this.enableMercadopago) return;

                switch (type) {
                    case 'diners':
                    case 'mastercard':
                    case 'visa':
                        this.creditCard.issuer = type;
                        break;
                    default:
                        this.creditCard.issuer = null;
                }
            },

            submit(callback) {
                this.checkPaymentIssuerMP(); // Caso o evento não tenha disparado

                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.$emit('error', 'Preencha todos os campos corretamente.');
                    return;
                }

                this.verifyingCard = true;

                window.dataLayer.push({
                    event: 'tentou-cartao',
                });

                if (this.enableMercadopago) {
                    this.handleMPCheckout(callback);
                } else {
                    callback({
                        module: 'cartoesdecredito',
                        ...this.creditCard,
                    });
                }
            },

            handleMPCheckout(callback) {
                if (typeof window.Mercadopago !== 'undefined') {
                    window.Mercadopago.setPublishableKey(
                        this.$scope.checkoutPreferences.mercado_pago_key
                    );
                } else {
                    this.$emit(
                        'error',
                        'Ocorreu uma falha durante a comunicação com o Masterpass. Por favor, recarregue a página ou tente outro método de pagamento.'
                    );
                    return;
                }

                window.Mercadopago.createToken(
                    {
                        email: this.customer.email,
                        cardNumber: this.creditCard.number,
                        securityCode: this.creditCard.cvv,
                        cardExpirationMonth: this.creditCard.expiryMonth,
                        cardExpirationYear: this.creditCard.expiryYear,
                        cardholderName: this.creditCard.name,
                        docType: this.customer.doc_type.toUpperCase(),
                        docNumber: this.customer.doc_number,
                    },
                    (status, response) => {
                        this.verifyingCard = false;

                        if (status !== 200 && status !== 201) {
                            this.$emit(
                                'error',
                                errorMessage(response.cause[0].code, this.customer, this.creditCard)
                            );
                            return;
                        }

                        callback({
                            module: 'cartoesdecredito',
                            ...this.creditCard,
                            mp: response,
                        });
                    }
                );
            },
        },

        validations: {
            creditCard: {
                name: { required },
                number: {
                    required,
                    minLength: minLength(14),
                },
                cvv: {
                    required,
                    minLength: minLength(3),
                    maxLength: maxLength(4),
                },
                expiryMonth: { required },
                expiryYear: { required },
                installments: {
                    required,
                    minValue: minValue(1),
                },
            },
        },

        created() {
            this.enableMercadopago = this.$scope.checkoutPreferences.mercado_pago;
        },

        mounted() {
            this.$v.$reset(); // Necessário para o campo do Cleave não inicializar como inválido
        },
    };
</script>
